import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mensagemDoCatalogo } from "../lib/adminCatalogo";
import { lerIndicadoresDoProduto, npsEmTexto, type IndicadoresDoProduto as Indicadores } from "../lib/pesquisas";

/**
 * Admin › Catálogo › Produtos › o produto › **Indicadores** (000310-000312 — o G1
 * do Plano de Melhorias da Equipe de Produtos, e a decisão 2 de Victor: "NPS
 * entra").
 *
 * Até 30/09 este arquivo trazia também as PESQUISAS de satisfação do produto.
 * Elas foram morar na aba Feedbacks, ao lado das avaliações das ferramentas
 * (`AdminFeedbacksPesquisas.tsx`, 000332), e o portão das funções delas foi
 * junto. Os indicadores ficaram: são os números da página do produto, com o NPS
 * do produto entre eles, e respondem ao Catálogo. O indicador do NPS aponta para
 * as pesquisas do curso, para quem vê a aba Feedbacks.
 *
 * ZERO regra aqui: os números são do banco. A tela escolhe o que mostrar
 * primeiro, e diz com letra o que o banco ainda não sabe, em vez de inventar.
 */

export function IndicadoresDoProduto({
  curso,
  alunos,
  faculdade,
  versao,
  vePesquisas = false,
}: {
  curso: string;
  /** Do CATÁLOGO (000290, pessoas distintas) — o mesmo número do alto da página. */
  alunos: number;
  faculdade: boolean;
  versao: number;
  /**
   * Quem vê a aba Feedbacks, onde as pesquisas moram desde a 000332: só para ele
   * o NPS ganha o caminho até elas — o painel recusaria a quem não vê a aba.
   */
  vePesquisas?: boolean;
}) {
  const [dados, setDados] = useState<Indicadores | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerIndicadoresDoProduto(curso);
      if (!vivo) return;
      if (r.ok) {
        setDados(r as Indicadores);
        setErro(null);
      } else setErro(mensagemDoCatalogo(r));
    })();
    return () => {
      vivo = false;
    };
  }, [curso, versao]);

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-indicadores" data-indicadores>
      <h3 id="titulo-indicadores" className="font-display text-lg font-bold text-titulo">
        Indicadores
      </h3>
      <p className="mt-1 text-sm text-cinza">
        Os números que o banco sabe responder hoje, medidos agora. O que o plano pede e ainda não tem
        definição está listado embaixo, com o porquê.
      </p>
      {erro && <p className="mt-3 text-sm text-erro-tinta" role="alert">{erro}</p>}
      {!dados && !erro && <p className="mt-3 text-sm text-cinza">Carregando…</p>}
      {dados && (
        <>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Numero rotulo="Alunos com acesso vigente" chave="alunos" valor={alunos.toLocaleString("pt-BR")} />
            <Numero
              rotulo="Matrículas"
              chave="matriculas"
              valor={dados.matriculas.ativas.toLocaleString("pt-BR")}
              apoio={
                dados.matriculas.total === 0
                  ? faculdade
                    ? "nenhuma matrícula ainda"
                    : "curso livre: não há matrícula"
                  : `ativas, de ${dados.matriculas.total.toLocaleString("pt-BR")} no total`
              }
            />
            <Numero
              rotulo="Certificados emitidos"
              chave="certificados"
              valor={dados.certificados_emitidos.toLocaleString("pt-BR")}
              apoio="os que valem: sem os recusados e os em conferência"
            />
            <Numero
              rotulo="Conteúdo produzido"
              chave="producao"
              valor={dados.producao.percentual === null ? "—" : `${dados.producao.percentual}%`}
              apoio={
                dados.producao.total === 0
                  ? "nenhuma aula no controle de produção"
                  : `${dados.producao.produzidas} de ${dados.producao.total} aula(s) aprovadas ou publicadas`
              }
            />
            <Numero
              rotulo="NPS do produto"
              chave="nps"
              valor={dados.nps ? npsEmTexto(dados.nps.nps) : "—"}
              apoio={
                dados.nps
                  ? `«${dados.nps.pesquisa}» · ${dados.nps.respostas} resposta(s)${dados.nps.aberta ? " · ainda aberta" : ""}`
                  : "nenhuma pesquisa de curso respondida ainda"
              }
              acao={
                vePesquisas ? (
                  <Link
                    to={`/admin/avaliacoes/pesquisas?curso=${encodeURIComponent(curso)}`}
                    className="font-semibold text-link underline underline-offset-2"
                    data-ver-pesquisas
                  >
                    As pesquisas do curso ficam em Feedbacks
                  </Link>
                ) : null
              }
            />
          </dl>
          {faculdade && (
            <p className="mt-4 text-sm text-tinta" data-professores-da-turma={dados.professores_da_turma_atual.length}>
              {dados.turma_atual === null ? (
                "O curso ainda não tem turma atual."
              ) : dados.professores_da_turma_atual.length === 0 ? (
                <>Turma atual: {dados.turma_atual}, sem professor na grade.</>
              ) : (
                <>
                  Turma atual: {dados.turma_atual}, com {dados.professores_da_turma_atual.length} professor(es) na
                  grade: {dados.professores_da_turma_atual.join(", ")}.
                </>
              )}
            </p>
          )}
          <details className="mt-4" data-a-definir={dados.a_definir.length}>
            <summary className="cursor-pointer text-sm font-semibold text-link">
              {dados.a_definir.length} indicador(es) do plano ainda sem definição
            </summary>
            <ul className="mt-2 divide-y divide-borda text-sm">
              {dados.a_definir.map((a) => (
                <li key={a.indicador} className="py-2">
                  <span className="font-semibold text-tinta">{a.indicador}</span>
                  <span className="text-cinza"> — {a.porque}</span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}

function Numero({
  rotulo,
  chave,
  valor,
  apoio,
  acao,
}: {
  rotulo: string;
  chave: string;
  valor: string;
  apoio?: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-borda p-4" data-indicador={chave}>
      <dt className="text-xs font-bold tracking-widest text-cinza uppercase">{rotulo}</dt>
      <dd className="mt-1 font-display text-2xl font-bold text-titulo" data-valor>
        {valor}
      </dd>
      {apoio && <dd className="mt-0.5 text-xs text-cinza">{apoio}</dd>}
      {acao && <dd className="mt-2 text-xs">{acao}</dd>}
    </div>
  );
}
